import React, { useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import Avatar from '../../../atoms/Avatar';
import { getBackgroundTheme, getTextType, getTextColor, getButtonTheme } from '../../../style';
import CreditsIcon from '../../../atoms/icons/CreditsIcon';
import { Link, useLocation } from 'react-router-dom';
import { sendPurchaseNavBuyCreditAction } from '../../../actions/purchaseActions';
import useMixPanel from '../../../hooks/useMixPanel';
import { useApp } from '../../../contexts/AppContext';
import BuyCTAIcon from '../../../atoms/icons/BuyCTAIcon';
import { FONT_FAMILIES } from '../../../theme/material/constants';
import breakpoints from '../../../theme/material/breakpoints';
import { MobileWrapper } from '../../common/appLayout/StyledElements';
import { CustomStyledProps } from 'style/style';

const NavItemContainer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  flex: 1,
});

const NavItem = styled('div')(
  {
    display: 'flex',
    alignItems: 'center',
    paddingTop: 0,
    cursor: 'pointer',
    color: '#FFF',
    [breakpoints.down('sm')]: {
      alignItems: 'unset',
      paddingTop: 8,
    },
  },
  ({ navHeight }: CustomStyledProps) =>
    navHeight && {
      [breakpoints.up('md')]: {
        height: navHeight,
      },
    }
);

const MobileCreditCountContainer = styled('div')({
  display: 'none',
  alignItems: 'center',
  paddingLeft: 0,
  marginRight: 15,
  [breakpoints.down('sm')]: {
    display: 'flex',
  },
});

const BuyButtonCTA = styled('span')({
  padding: '8px 14px',
  borderRadius: 6,
  height: 13,
  width: 47,
  background: getButtonTheme().gradientTeal.background,
  color: getButtonTheme().gradientTeal.text,
  '&:hover': {
    background: getButtonTheme().gradientTeal.hover.background,
    color: getButtonTheme().gradientTeal.hover.text,
  },
  '&:active': {
    background: getButtonTheme().gradientTeal.active.background,
    color: getButtonTheme().gradientTeal.active.text,
  },
  '&:focus': {
    background: getButtonTheme().gradientTeal.hover.background,
    color: getButtonTheme().gradientTeal.hover.text,
  },
});

const DotMenuIcon = styled('div')({
  marginLeft: 6,
  marginRight: 6,
  lineHeight: 0,
  [breakpoints.down(639.05)]: {
    marginLeft: 20,
  },
  [breakpoints.down('sm')]: {
    display: 'none',
  },
});

const StyledMobileAvatar = styled(Avatar)(
  {
    marginRight: 4,
    [breakpoints.down('sm')]: {
      display: 'none',
    },
  },

  ({ hideMobile }) =>
    hideMobile && {
      [breakpoints.down(639.05)]: {
        display: 'none',
      },
    }
);

const IconContainer = styled('div')({}, ({ marginRight, marginLeft }: CustomStyledProps) => ({
  marginRight: marginRight && '4px',
  marginLeft: marginLeft && '4px',
}));

const DesktopUserContainer = styled('div')({
  lineHeight: '18px',
  marginRight: '8px',
  marginLeft: '4px',
  [breakpoints.down('sm')]: {
    display: 'none',
  },
});

const Username = styled('span')(() => ({
  display: 'block',
  maxWidth: '110px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontFamily: FONT_FAMILIES.primary,
  fontSize: 13,
  fontWeight: 400,
  lineHeight: '18px' /* 138.462% */,
  letterSpacing: '-0.3px',
  color: '#F3FCFC',
}));

const Credits = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center',
  fontFamily: FONT_FAMILIES.primary,
  fontSize: 13,
  fontWeight: 400,
  lineHeight: '18px' /* 138.462% */,
  letterSpacing: '-0.3px',
  marginTop: 0,
  width: 'auto',
  color: '#F3FCFC',
}));

const BuyButtonCTALabel = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  fontSize: 13,
  color: '#FFF',
  lineHeight: '14px',
  letterSpacing: '-0.26px',
  fontWeight: 900,
  fontFamily: FONT_FAMILIES.primary,
});

const CreditsText = styled('span')(() => ({
  lineHeight: '18px',
  ...getTextType().caption,
  ...getTextColor().DARK_3,
  fontFamily: FONT_FAMILIES.primary,
}));

// width 100% - 120px
const StyledDesktopBuyCreditsButton = styled(Link)(() => ({
  border: 'none',
  display: 'flex',
  padding: '11px 12px',
  alignItems: 'center',
  marginTop: '13px',
  marginBottom: '13px',
  marginRight: '16px',
  borderRadius: '4px',
  width: '106px',
  justifyContent: 'center',
  maxWidth: '180px',
  fontSize: '15px',
  fontWeight: 900,
  whiteSpace: 'nowrap',
  fontFamily: FONT_FAMILIES.primary,
  background: getButtonTheme().gradientTeal.background,
  color: getButtonTheme().gradientTeal.text,
  '&:hover': {
    background: getButtonTheme().gradientTeal.hover.background,
    color: getButtonTheme().gradientTeal.hover.text,
  },
  '&:active': {
    background: getButtonTheme().gradientTeal.active.background,
    color: getButtonTheme().gradientTeal.active.text,
  },
  '&:focus': {
    background: getButtonTheme().gradientTeal.hover.background,
    color: getButtonTheme().gradientTeal.hover.text,
  },
  '@media screen and (max-width: 1100px)': {
    maxWidth: '125px',
  },
  '@media screen and (max-width: 800px)': {
    marginRight: '10px',
  },
  '@media screen and (max-width: 760px)': {
    display: 'none !important',
  },
  [breakpoints.down('sm')]: {
    display: 'none !important',
  },
}));

interface UserNavItemProps {
  clickHandler: any;
  credits: any;
  navHeight: any;
  avatar: any;
  username: any;
  canUpgrade: any;
  isMobile: any;
}

const UserNavItem = ({
  clickHandler,
  credits,
  navHeight,
  avatar,
  username,
  canUpgrade,
  isMobile,
}: UserNavItemProps): any => {
  const buyCredits = useRef(null);
  const { trackPackagesPageVisited } = useMixPanel();
  const { accountHashId } = useApp();
  const location = useLocation();

  useEffect(() => {
    const buyCreditsNode = buyCredits.current;

    if (buyCredits && buyCreditsNode) {
      buyCreditsNode.addEventListener(
        'click',
        () => {
          sendPurchaseNavBuyCreditAction();
          trackPackagesPageVisited({ Source: 'Buy Credits Header' });
        },
        false
      );

      return () => {
        buyCreditsNode.removeEventListener('click', () => null, false);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountHashId]);

  const isBillingPage = (): boolean => {
    return (
      location.pathname.indexOf('/payment') > -1 ||
      location.pathname.indexOf('/packages') > -1 ||
      location.pathname.indexOf('/billing') > -1
    );
  };

  if (isMobile && (isBillingPage() || !canUpgrade)) {
    return null;
  }

  return (
    <NavItemContainer>
      {canUpgrade ? (
        <StyledDesktopBuyCreditsButton
          ref={buyCredits}
          to="/packages"
          id="btn-buy-credits"
          onClick={() => {
            trackPackagesPageVisited({ Source: 'Buy Credits Header' });
          }}
        >
          BUY CREDITS
        </StyledDesktopBuyCreditsButton>
      ) : null}
      <NavItem
        data-test-id="submenu-show-hide"
        id="user-nav-item"
        navHeight={navHeight}
        onClick={() => {
          !isMobile && clickHandler(true);
        }}
      >
        {canUpgrade && !isBillingPage() ? (
          <MobileCreditCountContainer>
            <BuyButtonCTA data-test-id="cypress-credit-count-container">
              <Link ref={buyCredits} to="/packages">
                <BuyButtonCTALabel>
                  <BuyCTAIcon customStyle={{ marginRight: 6 }} />
                  {'BUY'}
                </BuyButtonCTALabel>
              </Link>
            </BuyButtonCTA>
          </MobileCreditCountContainer>
        ) : (
          <MobileWrapper style={{ padding: 25 }} />
        )}
        <StyledMobileAvatar src={avatar} alt="profile" width={32} hideMobile={canUpgrade} />
        <DesktopUserContainer>
          <Username>{username}</Username>
          {canUpgrade ? (
            <Credits>
              <IconContainer marginRight>
                <CreditsIcon />
              </IconContainer>
              <CreditsText data-test-id="header-profile-credits">{credits} credits</CreditsText>
            </Credits>
          ) : null}
        </DesktopUserContainer>
        <DotMenuIcon data-test-id="header-dot-menu">
          <svg xmlns="http://www.w3.org/2000/svg" width="8" height="33" viewBox="0 -4 4 33">
            <g fill={getBackgroundTheme().iconFill} fillRule="evenodd">
              <circle cx="2" cy="0" r="3" />
              <circle cx="2" cy="12" r="3" />
              <circle cx="2" cy="24" r="3" />
            </g>
          </svg>
        </DotMenuIcon>
      </NavItem>
    </NavItemContainer>
  );
};

export default UserNavItem;
